import { Spin, Select } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'hooks';
import { zPositions } from '@/constants';
import getBuyers from 'api/ContactController/getBuyers';
import errorReporter from '@app/errorReporter';
import { Text, Tag, Icon, theme } from 'components';
import { ContactController } from 'api';
import {
  ContactDrawerContext,
  ContactDrawerMode,
  ContactDrawerType,
} from 'contexts';

import {
  IClientContact,
  ClientContactTag,
  ContactType,
  ContactPhoneType,
} from '@bb/contacts';

import ConfigContactModal from '../../ConfigContactModal';
import { ContactStateType } from '../../ConfigContactModal/types';
import { ContactModalMode } from '../../ConfigContactModal/enums';
import { ConfigContactModalAngularComponent } from '../../ConfigContactModal/forAngular/ConfigConactModalAngularComponent';

export interface SearchResult {
  label: string;
  value: string;
}

type InviteBuyerProps = {
  agent?: any;
  value: SearchResult[];
  listing: any;
  $rootScope?: any;
  onValueChange: (buyers: SearchResult[]) => void;
};

const getBuyerResult = (contact: {
  firstName: string;
  lastName?: string;
  id?: string;
  contactId?: string;
}) => ({
  label: `${contact.firstName} ${contact.lastName}`,
  value: contact.id || contact.contactId,
});

const sortBuyerLabels = (
  buyer1: { label: string },
  buyer2: { label: string },
) => buyer1.label.localeCompare(buyer2.label);

async function fetchBuyersList(
  name: string,
  agentId?: string,
): Promise<SearchResult[]> {
  const result = await getBuyers(name, agentId);

  const sortedOptions: SearchResult[] = result.map(user =>
    getBuyerResult(user),
  );

  return sortedOptions.sort(sortBuyerLabels);
}

const InviteBuyer = ({
  agent,
  value,
  onValueChange,
  listing,
  $rootScope,
  ...props
}: InviteBuyerProps) => {
  const [query, setQuery] = useState('');
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [loading, setLoading] = useState(false);

  const { openContactDrawer } = useContext(ContactDrawerContext);

  const [options, setOptions] = useState<SearchResult[]>([]);

  const debounceQuery = useDebounce(query, 500);

  useEffect(() => {
    const getBuyersList = async () => {
      setLoading(true);
      try {
        const result = await fetchBuyersList(debounceQuery, agent?.id);
        setOptions(result);
      } catch (err) {
        errorReporter.error(err);
      }
      setLoading(false);
    };

    getBuyersList();

    return () => {
      setOptions([]);
    };
  }, [debounceQuery, agent?.id]);

  const onContactSave = async (
    contact: ContactStateType,
    contactModalMode: ContactModalMode,
  ) => {
    setSavingContact(true);
    let contactResult: SearchResult;

    const updatePayload = {
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      pronoun: contact.pronoun,
      phones: contact.phone
        .filter(phone => phone.number)
        .map(phone => ({
          number: phone.number,
          type: ContactPhoneType.Mobile,
        })),
      buyerNotifications: contact.notifications,
    };

    if (contactModalMode === ContactModalMode.AddExisting) {
      contactResult = getBuyerResult(contact);
      await ContactController.updateContact<IClientContact>(
        contact.contactId,
        updatePayload,
      );
    } else {
      const createPayload = {
        email: contact.email,
        firstName: contact.firstName,
        lastName: contact.lastName,
        pronoun: contact.pronoun,
        tags: [ClientContactTag.Buyer],
        type: contact.type,
        phones: contact.phone
          .filter(phone => phone.number)
          .map(phone => ({
            number: phone.number,
            type: ContactPhoneType.Mobile,
          })),
        listings: [] as { id: string; isContact: boolean }[],
        owner: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
        },
        buyerNotifications: contact.notifications,
      };

      const createdContact =
        await ContactController.createContactWithOwner<IClientContact>(
          ContactType.Client,
          createPayload,
        );
      contactResult = getBuyerResult(createdContact);
    }

    if (value.every(selected => selected.value !== contactResult.value)) {
      onValueChange([...value, contactResult].sort(sortBuyerLabels));
    }
    if (options.every(option => option.value !== contactResult.value)) {
      setOptions([...options, contactResult]);
    }

    setSavingContact(false);
    closeContactModal();
  };

  const closeContactModal = () => {
    setContactModalVisible(false);
  };

  const showSearch = value.length === 0;

  return (
    <StyledFlexContainer>
      {showSearch && <StyledSearchIcon type="search" width="16px" />}
      <StyledSelect
        mode="multiple"
        labelInValue
        filterOption={false}
        value={value}
        tagRender={prop => (
          <Tag
            onClick={() =>
              $rootScope
                ? $rootScope.openContactDrawer(
                    ContactDrawerMode.Profile,
                    ContactDrawerType.Contact,
                    prop.value as string,
                  )
                : openContactDrawer(
                    ContactDrawerMode.Profile,
                    ContactDrawerType.Contact,
                    prop.value as string,
                  )
            }
            color="blue"
            closable
            onClose={e => {
              const newValue = value.filter(v => v.value !== prop.value);
              onValueChange(newValue.sort(sortBuyerLabels));
              setQuery('');
            }}
          >
            {prop.label}
          </Tag>
        )}
        onChange={(newValue: SearchResult[]) => {
          onValueChange(newValue.sort(sortBuyerLabels));
          setQuery('');
        }}
        searchValue={query}
        placeholder="Search buyers"
        onSearch={searchQuery => setQuery(searchQuery)}
        notFoundContent={
          loading ? <Spin size="small" /> : <Text>No results</Text>
        }
        {...props}
        options={options}
        dropdownRender={menu => (
          <div>
            {menu}
            <StyledFixedColumn onClick={() => setContactModalVisible(true)}>
              <Icon width="12px" type="antdPlusOutlined" color="blue6" />
              <StyledText color="blue6">Add Buyer</StyledText>
            </StyledFixedColumn>
          </div>
        )}
        dropdownStyle={{
          display: contactModalVisible && 'none',
          zIndex: zPositions.inviteBuyer,
        }}
        $showSearch={showSearch}
      />
      {$rootScope ? (
        <ConfigContactModalAngularComponent
          visible={contactModalVisible}
          isSaving={savingContact}
          feedbackSendToSeller={false}
          addBuyerContact
          listing={listing}
          listingId={listing?.id}
          onClose={closeContactModal}
          onSave={onContactSave}
          $rootScope={$rootScope}
          // At invite buyer screen you can't select must call
          isMustCallDisabled={false}
        />
      ) : (
        <ConfigContactModal
          visible={contactModalVisible}
          isSaving={savingContact}
          feedbackSendToSeller={false}
          addBuyerContact
          listing={listing}
          listingId={listing?.id}
          onClose={closeContactModal}
          onSave={onContactSave}
          // At invite buyer screen you can't select must call
          isMustCallDisabled={false}
        />
      )}
    </StyledFlexContainer>
  );
};

export default InviteBuyer;

const StyledFlexContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledSelect = styled(Select)<{ $showSearch: boolean }>`
  width: 100%;
  position: relative;
  border-radius: 2px;
  .ant-select-selection-placeholder {
    padding: 0 0 0 25px !important;
  }
  .ant-select-selection-item {
    color: ${theme.blue6};
    border: 1px solid ${theme.blue6};
    border-radius: 0px;
    background-color: ${theme.blue1};
    .ant-select-selection-item-remove {
      .anticon {
        color: ${theme.blue6};
      }
    }
  }
  .ant-select-selection-overflow {
    ${({ $showSearch }) => $showSearch && 'margin: 0 0 0 25px !important'};
  }
`;

const StyledSearchIcon = styled(Icon)`
  position: absolute;
  left: 13px;
  z-index: 1;
  color: ${theme.gray8};
`;

const StyledFixedColumn = styled.div`
  padding: 5px 10px;
  color: ${theme.blue6} !important;
  display: flex;
  background-color: ${theme.gray3};
  align-items: center;
  :hover {
    cursor: pointer;
  }
`;

const StyledText = styled(Text)`
  font-size: 14px;
  color: ${theme.blue6} !important;
  margin-left: 5px;
`;
